export interface StreamSearchParams {
  keyword?: string;
  from?: string;
  to?: string;
  authorId?: string;
}

const EMPTY_STREAM_SEARCH_CRITERIA_SIGNATURE = "criterion:";

const normalizeCriteriaSignatureValue = (value: string | undefined): string =>
  value?.trim() || "";

export const buildStreamSearchCriteriaSignature = (
  params: StreamSearchParams | undefined | null,
): string => {
  if (!params) return EMPTY_STREAM_SEARCH_CRITERIA_SIGNATURE;

  const keyword = normalizeCriteriaSignatureValue(params.keyword);
  const authorId = normalizeCriteriaSignatureValue(params.authorId);
  const from = normalizeCriteriaSignatureValue(params.from);
  const to = normalizeCriteriaSignatureValue(params.to);

  return `criterion:keyword=${keyword};authorId=${authorId};from=${from};to=${to};`;
};

export type StreamSearchCriteriaState = {
  criteria: StreamSearchParams;
  hasCriteria: boolean;
};

const toIsoDate = (value: string | undefined, toEndOfDay = false) => {
  if (!value?.trim()) return undefined;
  const trimmed = value.trim();

  // Prefer the explicit YYYY-MM-DD prefix to avoid timezone offsets shifting
  // the intended calendar day.
  const datePrefixMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|[T\s])/);
  const match = datePrefixMatch ?? trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [, yearText, monthText, dayText] = match;
    const year = Number(yearText);
    const month = Number(monthText) - 1;
    const day = Number(dayText);
    const date = new Date(Date.UTC(year, month, day));
    date.setUTCHours(
      toEndOfDay ? 23 : 0,
      toEndOfDay ? 59 : 0,
      toEndOfDay ? 59 : 0,
      toEndOfDay ? 999 : 0,
    );
    return date.toISOString().replace(/\.\d{3}Z$/, "Z");
  }

  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  // Use local calendar components so date-only user intent is preserved.
  const normalized = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  normalized.setUTCHours(
    toEndOfDay ? 23 : 0,
    toEndOfDay ? 59 : 0,
    toEndOfDay ? 59 : 0,
    toEndOfDay ? 999 : 0,
  );
  return normalized.toISOString().replace(/\.\d{3}Z$/, "Z");
};

export const normalizeSearchQuery = (query: string): string => query.trim();

const normalizeFilterValue = (
  value: string | undefined,
): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

export const buildStreamSearchParams = (
  query: string,
  filters: StreamSearchParams = {},
): StreamSearchParams => {
  const params: StreamSearchParams = {};

  const normalizedKeyword = normalizeSearchQuery(query);
  if (normalizedKeyword) {
    params.keyword = normalizedKeyword;
  }

  const normalizedFrom = normalizeFilterValue(filters.from);
  if (normalizedFrom) {
    const from = toIsoDate(normalizedFrom, false);
    if (from) {
      params.from = from;
    }
  }

  const normalizedTo = normalizeFilterValue(filters.to);
  if (normalizedTo) {
    const to = toIsoDate(normalizedTo, true);
    if (to) {
      params.to = to;
    }
  }

  const normalizedAuthorId = normalizeFilterValue(filters.authorId);
  if (normalizedAuthorId) {
    params.authorId = normalizedAuthorId;
  }

  return params;
};

export const hasStreamSearchCriteria = (
  params: StreamSearchParams | undefined | null,
) => {
  if (!params) return false;
  return Boolean(params.keyword || params.authorId || params.from || params.to);
};

export const buildStreamSearchCriteriaState = (
  criteria: StreamSearchParams,
): StreamSearchCriteriaState => {
  return {
    criteria,
    hasCriteria: hasStreamSearchCriteria(criteria),
  };
};

export const createEmptyStreamSearchCriteriaState =
  (): StreamSearchCriteriaState => ({
    criteria: {},
    hasCriteria: false,
  });
